import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Gift, ArrowRight, ShieldCheck, ShieldAlert, Truck, CreditCard, Route as RouteIcon, Receipt, FileText, Share2, Copy, RefreshCw } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { ROLE_LABEL, formatDateTime, formatCHF } from "@/lib/format";
import { api, formatApiError } from "@/lib/api";
import RatingDisplay from "@/components/RatingDisplay";
import PullToRefreshContainer from "@/components/PullToRefreshContainer";
import DriverComplianceBanner from "@/components/DriverComplianceBanner";
import DriverOnboardingChecklist from "@/components/DriverOnboardingChecklist";
import PushNotificationsCard from "@/components/PushNotificationsCard";
import ShareTrackingProfileCard from "@/components/ShareTrackingProfileCard";
import SessionsCard from "@/components/SessionsCard";

const VEHICLE_LABELS = { moto: "Moto", scooter: "Scooter", auto: "Auto", auto_grande: "Auto grande", furgone: "Furgone" };

const DOC_STATUS_TEXT = {
  missing: { text: "Documenti mancanti", status: "todo" },
  incomplete: { text: "Documenti incompleti", status: "todo" },
  pending: { text: "In verifica", status: "warn" },
  verified_basic: { text: "Verificato base", status: "ok" },
  verified_full: { text: "Verificato completo", status: "ok" },
};

export default function DriverProfile() {
  const { user, refresh } = useAuth();
  if (!user) return null;
  const info = user.driver_info || {};
  const v = info.is_verified;
  const payoutStatus = info.payout_status || (info.payout_iban ? "pending" : "missing");
  const docs = DOC_STATUS_TEXT[info.documents_status] || DOC_STATUS_TEXT.missing;
  return (
    <PullToRefreshContainer onRefresh={async () => { await refresh(); toast.success("Profilo aggiornato"); }}>
    <div className="space-y-5" data-testid="driver-profile-page">
      <div>
        <p className="overline">Profilo</p>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tighter mt-1">Driver</h1>
      </div>

      <div className={`card-soft p-5 flex items-center gap-3 ${v ? "" : "border-amber-200 bg-amber-50"}`}>
        {v ? <ShieldCheck className="h-5 w-5 text-emerald-600" /> : <ShieldAlert className="h-5 w-5 text-amber-700" />}
        <div>
          <p className="font-semibold tracking-tight">{v ? "Account verificato" : "In attesa di verifica documenti"}</p>
          <p className="text-xs text-dapnow-muted">{v ? "Puoi accettare consegne." : "Un admin verificherà i tuoi documenti a breve."}</p>
        </div>
      </div>

      {/* Phase 43.27 — Checklist onboarding completa (spostata da "Disponibili") */}
      <DriverOnboardingChecklist />

      {/* Phase 43.12 — Compliance KYC banner */}
      <DriverComplianceBanner />

      {/* Quick actions */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
        <ActionCard to="/driver/vehicle" testid="profile-vehicle-link" icon={Truck} title="Veicolo" subtitle={info.vehicle_type ? VEHICLE_LABELS[info.vehicle_type] : "Da configurare"} status={info.vehicle_type ? "ok" : "todo"} />
        <ActionCard to="/driver/documents" testid="profile-documents-link" icon={FileText} title="Documenti" subtitle={docs.text} status={docs.status} />
        <ActionCard to="/driver/payout" testid="profile-payout-link" icon={CreditCard} title="Coordinate bancarie" subtitle={payoutStatus === "verified" ? "IBAN verificato" : payoutStatus === "pending" ? "In verifica" : "Facoltative · referente DapNow"} status={payoutStatus === "pending" ? "warn" : "ok"} />
        <div className="card-soft p-5 text-sm text-dapnow-muted" data-testid="profile-remuneration-note">La remunerazione dei driver interni è gestita da DapNow fuori da Stripe. Non devi aprire un account Stripe Connect.</div>
        <ActionCard to="/driver/routes" testid="profile-routes-link" icon={RouteIcon} title="Tragitti pendolari" subtitle="Casa↔lavoro · fino a 3" status="ok" />
        <ActionCard to="/driver/tax-summary" testid="profile-tax-link" icon={Receipt} title="Riepilogo importi" subtitle="Da riconciliare · PDF · CSV" status="ok" />
        <ActionCard to="/driver/referral" testid="profile-referral-link" icon={Gift} title="Referral" subtitle={`Bonus: ${formatCHF(user.earnings_bonus || 0)}`} featured />
        <ActionCard to="/driver/history" testid="profile-history-link" icon={Receipt} title="Storico consegne" subtitle="Tutte le tue consegne completate" />
      </div>

      <div className="card-soft p-6 sm:p-7 space-y-4">
        <Row k="Nome" v={user.name} />
        <Row k="Email" v={user.email} />
        <Row k="Telefono" v={user.phone || "—"} />
        <Row k="Ruolo" v={ROLE_LABEL[user.role]} />
        <Row k="Veicolo" v={info.vehicle_type ? `${VEHICLE_LABELS[info.vehicle_type]}${info.vehicle_brand ? ` · ${info.vehicle_brand} ${info.vehicle_model || ""}` : ""}` : <Link to="/driver/vehicle" className="text-dapnow-blue font-semibold">Configura ora</Link>} />
        <Row k="Targa" v={info.vehicle_plate || "—"} />
        <Row k="Patente" v={info.license_number || "—"} />
        <Row k="Codice referral" v={<span className="font-mono">{user.referral_code || "—"}</span>} />
        <Row k="Valutazione media" v={<RatingDisplay avg={user.avg_rating} count={user.ratings_count} />} />
        <Row k="Consegne completate" v={user.delivered_count || 0} />
        <Row k="Membro dal" v={formatDateTime(user.created_at)} />
      </div>

      <PublicShareCard />

      {/* Phase 44.21 — Quick access "Condividi tracking" ordini attivi
          (read-only per driver: vede solo link già generati dal cliente) */}
      <ShareTrackingProfileCard readOnly testid="driver-share-tracking-card" />

      {/* Phase 44.31 — Sicurezza · Sessioni attive */}
      <SessionsCard testid="driver-sessions-card" />

      <PushNotificationsCard />
    </div>
    </PullToRefreshContainer>
  );
}

function PublicShareCard() {
  const [token, setToken] = useState(null);
  const [busy, setBusy] = useState(false);

  const issue = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/driver/public-share/issue");
      setToken(data.token);
      toast.success("Link pubblico generato");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };
  const revoke = async () => {
    setBusy(true);
    try {
      await api.delete("/driver/public-share");
      setToken(null);
      toast.success("Link pubblico revocato");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };
  const url = token ? `${window.location.origin}/d/${token}` : null;
  const copy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copiato negli appunti");
    } catch { toast.error("Copia non disponibile"); }
  };

  return (
    <div className="card-soft p-5 sm:p-6 space-y-4" data-testid="public-share-card">
      <div className="flex items-start gap-3">
        <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-dapnow-blue-soft text-dapnow-blue shrink-0">
          <Share2 className="h-5 w-5" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="overline">Profilo pubblico</p>
          <h3 className="font-display text-lg font-semibold tracking-tight">Condividi un mini-profilo</h3>
          <p className="text-sm text-dapnow-muted mt-1">
            Genera un link <strong>solo lettura</strong> con il tuo nome, valutazione e tragitti pendolari.
            Nessun dato sensibile (email, telefono, IBAN) viene esposto.
          </p>
        </div>
      </div>
      {!token ? (
        <button onClick={issue} disabled={busy} data-testid="issue-share-btn" className="btn-primary">
          {busy ? "Generazione…" : "Genera link pubblico"}
        </button>
      ) : (
        <div className="space-y-2">
          <div className="flex items-center gap-2 rounded-2xl border border-dapnow-line bg-dapnow-bg px-3 py-2">
            <code className="text-xs flex-1 truncate" data-testid="public-share-url">{url}</code>
            <button onClick={copy} className="text-xs font-semibold text-dapnow-blue inline-flex items-center gap-1" data-testid="copy-share-btn">
              <Copy className="h-3 w-3" /> Copia
            </button>
          </div>
          <div className="flex gap-2 text-xs">
            <a href={url} target="_blank" rel="noreferrer" className="font-semibold text-dapnow-blue hover:underline" data-testid="open-share-link">Apri anteprima ↗</a>
            <button onClick={issue} disabled={busy} className="font-semibold text-dapnow-muted hover:text-dapnow-ink inline-flex items-center gap-1" data-testid="rotate-share-btn">
              <RefreshCw className="h-3 w-3" /> Rigenera (revoca il precedente)
            </button>
            <button onClick={revoke} disabled={busy} className="font-semibold text-dapnow-red hover:underline ml-auto" data-testid="revoke-share-btn">
              Revoca
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ActionCard({ to, testid, icon: Icon, title, subtitle, status, featured }) {
  const tone = featured
    ? "bg-gradient-to-br from-white to-dapnow-blue-soft"
    : "";
  return (
    <Link to={to} data-testid={testid} className={`card-soft p-5 hover:-translate-y-0.5 transition group ${tone}`}>
      <div className="flex items-center justify-between">
        <span className={`inline-flex h-10 w-10 items-center justify-center rounded-xl ${featured ? "bg-dapnow-blue text-white" : "bg-dapnow-blue-soft text-dapnow-blue"}`}>
          <Icon className="h-5 w-5" />
        </span>
        <ArrowRight className="h-4 w-4 text-dapnow-muted group-hover:translate-x-1 transition" />
      </div>
      <p className="overline mt-4">{title}</p>
      <p className="font-display text-base font-bold tracking-tight mt-0.5">{subtitle}</p>
      {status && (
        <span className={`mt-2 inline-flex pill ${status === "ok" ? "bg-emerald-50 text-emerald-700" : status === "warn" ? "bg-amber-50 text-amber-700" : "bg-red-50 text-red-700"}`}>
          {status === "ok" ? "OK" : status === "warn" ? "Attenzione" : "Da completare"}
        </span>
      )}
    </Link>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex justify-between items-baseline border-b border-dapnow-line pb-3 last:border-0 last:pb-0">
      <span className="overline text-[10px]">{k}</span>
      <span className="font-medium text-right">{v}</span>
    </div>
  );
}
